/**
 * Global "Confirm it's you" modal (CAP-AC-037). Mounted ONCE at the app root; driven by
 * the step-up controller, which the HTTP layer awaits.
 */

import React, { useEffect, useState, useSyncExternalStore } from 'react'

import { useTranslation } from '@platform/i18n/hooks/useTranslation'

import Button from '@/shared/components/ui/Button/Button'
import Input from '@/shared/components/ui/Input/Input'
import Modal from '@/shared/components/ui/Modal/Modal'

import {
  cancelStepUp,
  getStepUpState,
  submitCredential,
  subscribeToStepUp,
} from './step-up-controller'

const KNOWN_ERRORS = ['INVALID_PASSWORD', 'INVALID_MFA_CODE', 'ACCOUNT_LOCKED'] as const

export const StepUpModal: React.FC = () => {
  const { t } = useTranslation('common')
  const state = useSyncExternalStore(subscribeToStepUp, getStepUpState, getStepUpState)
  const [useCode, setUseCode] = useState(false)
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')

  useEffect(() => {
    if (!state.open) {
      setUseCode(false)
      setPassword('')
      setCode('')
    }
  }, [state.open])

  const valid = useCode ? /^\d{6}$/.test(code) : password.length > 0

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!valid || state.busy) return
    void submitCredential(useCode ? { code } : { password })
  }

  const errorKey = KNOWN_ERRORS.find((known) => known === state.errorCode)

  return (
    <Modal
      isOpen={state.open}
      onClose={cancelStepUp}
      title={t('stepUp.title')}
      description={t('stepUp.explanation')}
      size="sm"
      busy={state.busy}
    >
      <form onSubmit={submit} data-testid="step-up-form">
        <Modal.Body>
          {state.locked ? null : useCode ? (
            <div>
              <label htmlFor="step-up-code">{t('stepUp.codeLabel')}</label>
              <Input
                id="step-up-code"
                name="code"
                type="text"
                value={code}
                placeholder="123456"
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
            </div>
          ) : (
            <div>
              <label htmlFor="step-up-password">{t('stepUp.passwordLabel')}</label>
              <Input
                id="step-up-password"
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          )}
          {errorKey ? (
            <p role="alert" data-testid="step-up-error">
              {t(`stepUp.errors.${errorKey}`)}
            </p>
          ) : state.errorCode ? (
            <p role="alert" data-testid="step-up-error">
              {t('stepUp.errors.UNKNOWN')}
            </p>
          ) : null}
          {state.locked ? null : (
            <Button type="button" variant="ghost" size="sm" onClick={() => setUseCode(!useCode)}>
              {useCode ? t('stepUp.usePassword') : t('stepUp.useCode')}
            </Button>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={cancelStepUp} disabled={state.busy}>
            {state.locked ? t('stepUp.close') : t('stepUp.cancel')}
          </Button>
          {state.locked ? null : (
            <Button type="submit" variant="primary" loading={state.busy} disabled={!valid}>
              {t('stepUp.confirm')}
            </Button>
          )}
        </Modal.Footer>
      </form>
    </Modal>
  )
}

export default StepUpModal
